<script lang="ts">
	// Stores
	import { getDeviceStatus, getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import { routeToDeviceSettings } from 'src/stores/ui/route-to-device-settings';
	import { setSidePanelState } from 'src/components/Sidepanel/sidepanel-helpers';

	// Configs
	import { devices_config } from 'src/configs/devices';

	// Helpers
	import { real_id } from 'src/helpers/interface_helpers';
	import { status_configurator } from 'src/helpers/device_data_helpers';
	import { hasVirtualGroups, isPillSerialMode } from 'src/helpers/thepill/thepill_data_helpers';
	import {
		getCriticalNotifications,
		getNotificationsForDevice,
	} from 'src/helpers/notifications/device_notifications_helper';
	import { getDeviceChartsConfig } from 'src/helpers/statistics/config/device_chart_config';
	import { useNavigateToHistoryStatisticsTab } from 'src/components/Sidepanel/useNavigateToHistoryStatisticsTab';

	// Design System Components
	import { Label } from '@Shelly/shelly-design-system';

	// Components
	import DeviceViewStateWrapper from 'src/components/Sidepanel/DeviceViews/DeviceViewStateWrapper.svelte';
	import NoPeripheralsGuide from 'src/components/ThePill/NoPeripheralsGuide.svelte';
	import PillDrawerPeripheralsV2 from 'src/components/ThePill/PillDrawerPeripheralsV2.svelte';
	import ThumbLayerChart from 'src/components/Chart/LayerChart/ThumbLayerChart.svelte';
	import PillDrawerControl from 'src/components/ThePill/PillDrawerControl.svelte';
	import PillDrawerMainStatusV2 from 'src/components/ThePill/PillDrawerMainStatusV2.svelte';

	// Types
	import type { Device } from 'src/types/Device';
	import DeviceNotificationLabel from '../Sidepanel/DeviceNotificationLabel.svelte';
	import {
		createGen1StatusLabels,
		getGen1DeviceStatuses,
	} from '../Sidepanel/SidePanelLayoutProvider/SidePanelDeviceLayout/Devices/Shelly/Gen1/gen1-main-helpers';
	import DeviceLabelList from '../Sidepanel/DeviceViews/DeviceLabelList.svelte';
	import VGroupComponentsV2 from '../DevicesSettings/VirtualComponents/ComponentsV2/VGroupComponentsV2.svelte';

	export let device: Device;

	$: statusStore = getDeviceStatus(device?.id);
	$: status = $statusStore;
	$: settingsStore = getDeviceSettings(device?.id);
	$: settings = $settingsStore;
	$: deviceConfig = devices_config?.[device?.type];

	$: _statusesRaw =
		status?.online && deviceConfig ? status_configurator(status, device, settings) : [];
	$: statuses = Array.isArray(_statusesRaw) ? _statusesRaw : [];

	$: _statuses = getGen1DeviceStatuses(status, device, settings, deviceConfig);
	$: labelList = createGen1StatusLabels(_statuses);

	$: pillMode = settings?.pill?.mode || null;
	$: outputs = statuses.filter((i) => i.peripheralType === 'switch') || [];
	$: sensors = statuses.filter(
		(i) => i.peripheralType !== 'switch' && i.peripheralType !== 'humidity',
	);
	$: mainLabel = statuses.find((i) => i?.drawer_main);

	$: showVirtualGroups = isPillSerialMode(pillMode) && hasVirtualGroups(settings);
	$: showGuide =
		(!statuses?.length && !showVirtualGroups) ||
		(mainLabel?.value && mainLabel?.value.includes('N/A'));

	$: deviceChartsConfig = device ? getDeviceChartsConfig(device) : [];
	$: showChart = deviceChartsConfig.length && (sensors.length < 2 || !outputs.length);
	$: criticalNotifications = getCriticalNotifications(getNotificationsForDevice(device?.id));

	const { navigateToHistoryStatisticsTab } = useNavigateToHistoryStatisticsTab();
	const handleOnClickThumbChart = () => navigateToHistoryStatisticsTab();

	const handleOpenPeripheralsSettings = () => {
		routeToDeviceSettings.setTarget(
			device.id,
			'pill_peripheral_settings',
			'pill_peripheral_settings',
		);
		setSidePanelState({ showSettingsModal: true });
	};
</script>

<DeviceViewStateWrapper {device}>
	<div slot="staticTop">
		<DeviceLabelList {labelList} />
		<DeviceNotificationLabel deviceId={device?.id} />
	</div>
	<div slot="content" class="flex-full-height justify-content-end">
		{#if criticalNotifications?.length}
			<div class="d-flex justify-content-center mb-s max-width-full">
				<Label
					class="max-width-full"
					size="m"
					value={criticalNotifications[0].name}
					type={criticalNotifications[0].type === 'danger' ? 'danger' : 'warning'}
					icon="fa-solid fa-shield-exclamation"
				/>
			</div>
		{/if}

		{#if showGuide}
			<NoPeripheralsGuide
				mode={pillMode}
				{device}
				{status}
				{settings}
				onPeripheralsSettingsClick={handleOpenPeripheralsSettings}
			/>
		{/if}

		{#if showVirtualGroups}
			<VGroupComponentsV2 {device} />
		{/if}
		<div class="mb-s flex-full-height justify-content-end">
			<PillDrawerPeripheralsV2 {sensors} on:click={handleOnClickThumbChart} />
		</div>

		{#if mainLabel}
			{#if showChart}
				<ThumbLayerChart
					class="mb-s"
					{device}
					{deviceChartsConfig}
					on:click={handleOnClickThumbChart}
				/>
			{/if}
		{/if}
		<PillDrawerMainStatusV2 {pillMode} {statuses} {mainLabel} />
		{#if outputs.length}
			<PillDrawerControl {device} {status} {outputs} useV2Components={true} />
		{/if}
	</div>
</DeviceViewStateWrapper>
